<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import TeleportWithDirection from 'dashboard/components-next/TeleportWithDirection.vue';
import FocusSpotlight from 'dashboard/components-next/sidebar/FocusSpotlight.vue';
import { useFocusSpotlight } from 'dashboard/composables/useFocusSpotlight';
import { usePageCoach } from 'dashboard/composables/usePageCoach';
import {
  screenIconFor,
  sectionIconFor,
  worldLabelKeyFor,
} from 'dashboard/helper/onboardingRegistry';
import { spotSelector, staggerDelayMs } from './manualMarkup';
import ManualItem from './ManualItem.vue';

const MOBILE_MQ = '(max-width: 767px)';
const REDUCE_MQ = '(prefers-reduced-motion: reduce)';

const { t, tm } = useI18n();
const route = useRoute();
const {
  manualCoach: currentCoach,
  markSeen,
  manualOpen,
  closeManual,
} = usePageCoach({
  host: false,
});
const { spotlightStyle, setSelector, measure, attach, detach } =
  useFocusSpotlight();

const panelRef = ref(null);
const scrollerRef = ref(null);
const activeSection = ref(0);
const reduceMotion = ref(false);
const isMobile = ref(false);
const showShortcuts = ref(false);

let previousFocus = null;
let onDocKeydown = null;
let reduceMq = null;
let mobileMq = null;
let onReduceChange = null;
let onMobileChange = null;
let sectionObserver = null;
let previousOverflow = '';

const isOpen = computed(
  () => manualOpen.value && Boolean(currentCoach.value?.manualKey)
);

const title = computed(() => {
  const coach = currentCoach.value;
  if (!coach?.titleKey) return '';
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return t(coach.titleKey);
});

const category = computed(() => {
  const key = worldLabelKeyFor(currentCoach.value?.key);
  if (!key) return '';
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return t(key);
});

const screenIcon = computed(() => screenIconFor(currentCoach.value?.key));

const intro = computed(() => {
  const key = currentCoach.value?.manualKey;
  if (!key) return '';
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  return t(`${key}.INTRO`);
});

const sections = computed(() => {
  const key = currentCoach.value?.manualKey;
  if (!key) return [];
  // eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
  const raw = tm(`${key}.SECTIONS`);
  return Array.isArray(raw) ? raw : [];
});

const spotlightOn = computed(() => {
  const style = spotlightStyle.value;
  return Boolean(style?.width) && !isMobile.value;
});

const itemDelay = (sectionIndex, itemIndex) => {
  let offset = 0;
  sections.value.slice(0, sectionIndex).forEach(section => {
    offset += Array.isArray(section?.ITEMS) ? section.ITEMS.length : 0;
  });
  return staggerDelayMs(offset + itemIndex, reduceMotion.value);
};

const focusables = () => {
  const root = panelRef.value;
  if (!root) return [];
  return [
    ...root.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    ),
  ].filter(node => !node.hasAttribute('disabled'));
};

const restoreFocus = () => {
  if (previousFocus && typeof previousFocus.focus === 'function') {
    previousFocus.focus();
  }
  previousFocus = null;
};

const focusPanel = () => {
  nextTick(() => {
    const closeButton = panelRef.value?.querySelector(
      '[data-testid="page-manual-close"]'
    );
    if (closeButton) {
      closeButton.focus();
      return;
    }
    panelRef.value?.focus();
  });
};

const lockScroll = () => {
  if (typeof document === 'undefined') return;
  previousOverflow = document.body.style.overflow;
  document.body.style.overflow = 'hidden';
};

const unlockScroll = () => {
  if (typeof document === 'undefined') return;
  document.body.style.overflow = previousOverflow;
};

const clearSpotlight = () => {
  detach();
  setSelector('');
};

const onSpotEnter = id => {
  if (isMobile.value) return;
  const selector = spotSelector(id);
  if (!selector) return;
  attach([selector]);
  setSelector(selector);
  measure();
};

const onSpotLeave = () => {
  clearSpotlight();
};

const onClose = () => {
  closeManual();
};

const onGotIt = () => {
  const coach = currentCoach.value;
  if (coach) markSeen(coach.key, coach.version);
  onClose();
};

const onSeeShortcuts = () => {
  const native = document.querySelector('[data-testid="chat-list-shortcuts"]');
  if (native && typeof native.click === 'function') {
    native.click();
  }
};

const scrollToSection = index => {
  const scroller = scrollerRef.value;
  const section = scroller?.querySelector(`#manual-section-${index}`);
  if (!scroller || !section) return;
  const top =
    section.getBoundingClientRect().top -
    scroller.getBoundingClientRect().top +
    scroller.scrollTop;
  scroller.scrollTo({
    top: Math.max(0, top - 8),
    behavior: reduceMotion.value ? 'auto' : 'smooth',
  });
};

const bindSectionObserver = () => {
  sectionObserver?.disconnect();
  sectionObserver = null;
  const scroller = scrollerRef.value;
  if (!scroller || typeof IntersectionObserver === 'undefined') return;
  sectionObserver = new IntersectionObserver(
    entries => {
      const visible = entries
        .filter(entry => entry.isIntersecting)
        .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
      if (!visible?.target?.id) return;
      const index = Number(visible.target.id.replace('manual-section-', ''));
      if (Number.isInteger(index)) activeSection.value = index;
    },
    { root: scroller, threshold: [0.35, 0.6] }
  );
  scroller.querySelectorAll('[id^="manual-section-"]').forEach(node => {
    sectionObserver.observe(node);
  });
};

onDocKeydown = event => {
  if (!isOpen.value) return;
  if (event.key === 'Escape') {
    event.preventDefault();
    event.stopPropagation();
    onClose();
    return;
  }
  if (event.key !== 'Tab') return;
  const nodes = focusables();
  if (!nodes.length) return;
  const first = nodes[0];
  const last = nodes[nodes.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
};

const attachDoc = () => {
  if (typeof document === 'undefined') return;
  document.addEventListener('keydown', onDocKeydown, true);
};

const detachDoc = () => {
  if (typeof document === 'undefined') return;
  document.removeEventListener('keydown', onDocKeydown, true);
};

const detachMedia = () => {
  if (reduceMq && onReduceChange) {
    if (reduceMq.removeEventListener) {
      reduceMq.removeEventListener('change', onReduceChange);
    } else {
      reduceMq.removeListener(onReduceChange);
    }
  }
  if (mobileMq && onMobileChange) {
    if (mobileMq.removeEventListener) {
      mobileMq.removeEventListener('change', onMobileChange);
    } else {
      mobileMq.removeListener(onMobileChange);
    }
  }
  reduceMq = null;
  mobileMq = null;
  onReduceChange = null;
  onMobileChange = null;
};

const syncMedia = () => {
  if (typeof window === 'undefined' || !window.matchMedia) return;
  detachMedia();
  reduceMq = window.matchMedia(REDUCE_MQ);
  mobileMq = window.matchMedia(MOBILE_MQ);
  reduceMotion.value = Boolean(reduceMq.matches);
  isMobile.value = Boolean(mobileMq.matches);
  onReduceChange = () => {
    reduceMotion.value = Boolean(reduceMq.matches);
  };
  onMobileChange = () => {
    isMobile.value = Boolean(mobileMq.matches);
    if (isMobile.value) clearSpotlight();
  };
  if (reduceMq.addEventListener) {
    reduceMq.addEventListener('change', onReduceChange);
    mobileMq.addEventListener('change', onMobileChange);
  } else {
    reduceMq.addListener(onReduceChange);
    mobileMq.addListener(onMobileChange);
  }
};

watch(
  isOpen,
  open => {
    if (open) {
      const coach = currentCoach.value;
      if (coach) markSeen(coach.key, coach.version);
      previousFocus = previousFocus || document.activeElement;
      showShortcuts.value = Boolean(
        typeof document !== 'undefined' &&
          document.querySelector('[data-testid="chat-list-shortcuts"]')
      );
      attachDoc();
      lockScroll();
      focusPanel();
      nextTick(() => bindSectionObserver());
      return;
    }
    clearSpotlight();
    sectionObserver?.disconnect();
    sectionObserver = null;
    detachDoc();
    unlockScroll();
    restoreFocus();
    // O botao que abriu o manual vive dentro da dica, que e desmontada ao abrir
    // (markSeen). Se o alvo guardado sumiu, manda o foco para o item de ajuda do
    // cluster: e o caminho permanente para reabrir esta tela.
    nextTick(() => {
      const ativo = document.activeElement;
      if (ativo && ativo !== document.body) return;
      const destino =
        document.querySelector('[data-testid="focus-mode-menu-toggle"]') ||
        document.querySelector('main');
      if (destino instanceof HTMLElement) {
        if (destino.tabIndex < 0) destino.tabIndex = -1;
        destino.focus();
      }
    });
  },
  { immediate: true }
);

watch(
  () => currentCoach.value?.manualKey,
  key => {
    if (manualOpen.value && !key) closeManual();
  }
);

// Trocar de tela fecha o manual. Sem isto o painel fica aberto trocando o
// conteudo por baixo: o IntersectionObserver segue observando as secoes da tela
// anterior, o indice destaca a secao errada, o scroll fica no meio e a dica da
// tela nova abre presa debaixo do backdrop, invisivel e inalcancavel.
watch(
  () => route?.name,
  (nova, anterior) => {
    if (nova !== anterior && manualOpen.value) closeManual();
  }
);

onMounted(() => {
  syncMedia();
});

onBeforeUnmount(() => {
  clearSpotlight();
  sectionObserver?.disconnect();
  detachDoc();
  detachMedia();
  unlockScroll();
});
</script>

<template>
  <TeleportWithDirection to="body">
    <div
      v-if="isOpen"
      class="fixed inset-0 z-[90]"
      data-testid="page-manual-root"
      :data-reduce-motion="reduceMotion ? 'true' : 'false'"
    >
      <FocusSpotlight v-if="spotlightOn" :style-value="spotlightStyle" />
      <button
        type="button"
        class="absolute inset-0"
        :class="spotlightOn ? 'bg-transparent' : 'bg-n-slate-12/40'"
        :aria-label="t('ONBOARDING.MANUAL.CLOSE')"
        @click="onClose"
      />
      <aside
        ref="panelRef"
        role="dialog"
        aria-modal="true"
        tabindex="-1"
        :aria-label="t('ONBOARDING.MANUAL.ARIA_LABEL')"
        aria-labelledby="page-manual-title"
        class="absolute inset-y-0 end-0 z-[80] flex w-full flex-col border-s border-n-weak bg-n-solid-1 shadow-xl outline-none md:w-[28rem]"
        data-testid="page-manual"
        @click.stop
      >
        <header
          class="flex shrink-0 items-start gap-3 border-b border-n-weak px-5 py-4"
        >
          <span
            class="mt-0.5 inline-flex size-11 shrink-0 items-center justify-center rounded-2xl bg-n-alpha-2 text-n-iris-11"
            aria-hidden="true"
          >
            <span class="size-5" :class="screenIcon" />
          </span>
          <div class="min-w-0 flex-1">
            <p
              v-if="category"
              class="m-0 mb-0.5 text-[11px] font-semibold uppercase tracking-wide text-n-slate-10"
            >
              {{ category }}
            </p>
            <h2
              id="page-manual-title"
              class="m-0 text-base font-semibold text-n-slate-12"
            >
              {{ title }}
            </h2>
          </div>
          <button
            type="button"
            class="flex size-11 shrink-0 items-center justify-center rounded-full text-n-slate-11 hover:bg-n-alpha-2"
            data-testid="page-manual-close"
            :aria-label="t('ONBOARDING.MANUAL.CLOSE')"
            @click="onClose"
          >
            <span class="i-ri-close-line size-5" aria-hidden="true" />
          </button>
        </header>
        <nav
          v-if="sections.length > 1"
          class="flex shrink-0 gap-2 overflow-x-auto border-b border-n-weak px-5 py-3"
          :aria-label="t('ONBOARDING.MANUAL.SECTION_NAV')"
          data-testid="page-manual-index"
        >
          <button
            v-for="(section, index) in sections"
            :key="index"
            type="button"
            class="inline-flex min-h-11 shrink-0 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold"
            :class="
              activeSection === index
                ? 'border-n-iris-6 bg-n-iris-9/15 text-n-iris-11'
                : 'border-n-weak bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12'
            "
            :data-testid="`page-manual-index-${index}`"
            @click="scrollToSection(index)"
          >
            <span
              class="size-3.5"
              :class="sectionIconFor(section.TITLE)"
              aria-hidden="true"
            />
            {{ section.TITLE }}
          </button>
        </nav>
        <div
          ref="scrollerRef"
          class="min-h-0 flex-1 overflow-y-auto px-5 py-4"
          data-testid="page-manual-scroller"
        >
          <p class="m-0 mb-5 text-sm leading-relaxed text-n-slate-11">
            {{ intro }}
          </p>
          <section
            v-for="(section, index) in sections"
            :id="`manual-section-${index}`"
            :key="index"
            class="mb-7 last:mb-0"
          >
            <div
              class="mb-3 flex items-center gap-2 border-b border-n-weak pb-2"
            >
              <span
                class="inline-flex size-7 items-center justify-center rounded-lg bg-n-alpha-2 text-n-iris-11"
                aria-hidden="true"
              >
                <span class="size-3.5" :class="sectionIconFor(section.TITLE)" />
              </span>
              <h3
                class="m-0 text-xs font-semibold uppercase tracking-wide text-n-slate-12"
              >
                {{ section.TITLE }}
              </h3>
            </div>
            <ul class="m-0 flex list-none flex-col gap-3 p-0">
              <ManualItem
                v-for="(item, itemIndex) in section.ITEMS"
                :key="itemIndex"
                :text="item"
                :delay-ms="itemDelay(index, itemIndex)"
                :reduce-motion="reduceMotion"
                @spot-enter="onSpotEnter"
                @spot-leave="onSpotLeave"
              />
            </ul>
          </section>
        </div>
        <footer
          class="flex shrink-0 flex-col gap-2 border-t border-n-weak px-5 py-4"
        >
          <button
            type="button"
            class="inline-flex min-h-11 w-full items-center justify-center rounded-full bg-n-slate-12 px-4 text-sm font-semibold text-n-background hover:opacity-90"
            data-testid="page-manual-got-it"
            @click="onGotIt"
          >
            {{ t('ONBOARDING.OK') }}
          </button>
          <button
            v-if="showShortcuts"
            type="button"
            class="inline-flex min-h-11 w-full items-center justify-center rounded-full px-4 text-sm font-medium text-n-slate-11 hover:bg-n-alpha-2"
            data-testid="page-manual-shortcuts"
            @click="onSeeShortcuts"
          >
            {{ t('ONBOARDING.MANUAL.SEE_SHORTCUTS') }}
          </button>
        </footer>
      </aside>
    </div>
  </TeleportWithDirection>
</template>
